import type {
  ApiStreamEvent,
  ApiStreamResult,
  BackendRouteDefinition,
  InvokeApiStreamRouteProps,
  InvokeHttpRouteProps,
} from './types';

export type SelectedTransport =
  | 'callable'
  | 'callableStream'
  | 'api'
  | 'api-stream';

interface CreateBackendRouteInvokerProps<
  TRouteTable extends Record<string, BackendRouteDefinition>,
  TStreamOptions = never,
> {
  routeTable: TRouteTable;
  invokeCallableRoute: <TResponse, TRequest>(
    callableName: string,
    requestData?: TRequest,
  ) => Promise<TResponse>;
  invokeHttpRoute: <TResponse>(
    props: InvokeHttpRouteProps,
  ) => Promise<TResponse>;
  invokeCallableStreamRoute?: <TResponse, TRequest>(
    callableName: string,
    requestData?: TRequest,
    options?: TStreamOptions,
  ) => Promise<TResponse>;
  // Optional so apps that have no api-stream routes (clients/admin/public
  // today) can omit it. The dispatcher throws at invoke time if a route
  // requires this transport but no invoker was supplied.
  invokeApiStreamRoute?: <TEvent extends ApiStreamEvent = ApiStreamEvent>(
    props: InvokeApiStreamRouteProps,
  ) => Promise<ApiStreamResult<TEvent>>;
}

export const resolveTransportForRoute = (
  route: BackendRouteDefinition,
): SelectedTransport => {
  return route.transport;
};

export const createBackendRouteInvoker =
  <
    TRouteTable extends Record<string, BackendRouteDefinition>,
    TStreamOptions = never,
  >({
    routeTable,
    invokeCallableRoute,
    invokeHttpRoute,
    invokeCallableStreamRoute,
    invokeApiStreamRoute,
  }: CreateBackendRouteInvokerProps<TRouteTable, TStreamOptions>) =>
  async <TResponse, TRequest = undefined>(
    routeKey: keyof TRouteTable,
    requestData?: TRequest,
    streamOptions?: TStreamOptions,
  ): Promise<TResponse> => {
    const route = routeTable[routeKey];
    const selectedTransport = resolveTransportForRoute(route);

    if (selectedTransport === 'callable') {
      if (route.transport !== 'callable') {
        throw new Error(
          `Route "${String(routeKey)}" cannot be invoked via callable transport`,
        );
      }

      return invokeCallableRoute<TResponse, TRequest>(
        route.callableName,
        requestData,
      );
    }

    if (selectedTransport === 'api') {
      if (route.transport !== 'api') {
        throw new Error(
          `Route "${String(routeKey)}" cannot be invoked via api transport`,
        );
      }

      const params = route.params?.(requestData);
      const query = route.query?.(requestData);
      const headers = route.headers?.(requestData);

      return invokeHttpRoute<TResponse>({
        method: route.method,
        path: route.path,
        params,
        query,
        headers,
        ...(route.body ? { body: route.body(requestData) } : {}),
        requestData,
      });
    }

    if (selectedTransport === 'callableStream') {
      if (route.transport !== 'callableStream') {
        throw new Error(
          `Route "${String(routeKey)}" cannot be invoked via callableStream transport`,
        );
      }

      if (!invokeCallableStreamRoute) {
        throw new Error(
          `Route "${String(routeKey)}" does not have a configured callableStream transport`,
        );
      }

      return invokeCallableStreamRoute<TResponse, TRequest>(
        route.callableName,
        requestData,
        streamOptions,
      );
    }

    if (selectedTransport === 'api-stream') {
      if (route.transport !== 'api-stream') {
        throw new Error(
          `Route "${String(routeKey)}" cannot be invoked via api-stream transport`,
        );
      }

      if (!invokeApiStreamRoute) {
        throw new Error(
          `Route "${String(routeKey)}" does not have a configured api-stream transport`,
        );
      }

      const params = route.params?.(requestData);
      const query = route.query?.(requestData);
      const headers = route.headers?.(requestData);

      // The api-stream invoker returns an `ApiStreamResult<TEvent>`. We cast to
      // the caller's `TResponse` so consumers can declare a precise per-route
      // event shape (e.g. `ApiStreamResult<{ data: ChatChunk }>`) at the call
      // site without the dispatcher needing to know about it.
      return invokeApiStreamRoute({
        method: route.method,
        path: route.path,
        params,
        query,
        headers,
        ...(route.body ? { body: route.body(requestData) } : {}),
        requestData,
      }) as Promise<TResponse>;
    }

    throw new Error('Unsupported backend transport');
  };
